import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Gift, Copy, Check, Trophy, Sparkles, Clock, Users, MessageCircle, Mail, Twitter } from 'lucide-react';
import { toast } from 'sonner';

export default function ReferAndEarn() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const load = async () => {
    try {
      const [me, lb] = await Promise.all([apiGet('/referrals/me'), apiGet('/referrals/leaderboard?limit=10')]);
      setData(me);
      setBoard(lb.leaderboard || []);
    } catch { toast.error('Could not load Refer & Earn'); }
  };
  useEffect(() => { load(); }, []);

  const shareUrl = data?.share_url?.startsWith('http') ? data.share_url : (typeof window !== 'undefined' ? `${window.location.origin}/?ref=${data?.code || ''}` : data?.share_url || '');

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success('Link copied'); setTimeout(() => setCopied(false), 2000); } catch { toast.error('Copy failed — long-press to copy'); }
  };

  const message = data?.promo?.share_message
    ? `${data.promo.share_message} ${shareUrl}`
    : `Hey! I'm using Gurukul Veda — a personal AI study companion that's super patient with the silliest doubts. Join free using my link and we both win: ${shareUrl}`;
  const shareTo = (channel) => {
    const u = encodeURIComponent(shareUrl);
    const m = encodeURIComponent(message);
    const links = {
      whatsapp: `https://wa.me/?text=${m}`,
      email: `mailto:?subject=${encodeURIComponent('Try Gurukul Veda with me')}&body=${m}`,
      twitter: `https://twitter.com/intent/tweet?text=${m}&url=${u}`,
    };
    window.open(links[channel], '_blank', 'noopener,noreferrer');
  };

  const redeem = async () => {
    setRedeeming(true);
    try {
      const r = await apiPost('/referrals/redeem', {});
      toast.success(`Done — Growth extended by ${r.days_added} days. New expiry: ${new Date(r.new_expiry).toLocaleDateString('en-IN')}`);
      load();
    } catch (e) { toast.error(e?.message || 'Could not redeem'); }
    setRedeeming(false);
  };

  if (!data) return <div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const { code, balance, history, rules } = data;
  const progressPct = Math.min(100, Math.round((balance.balance / rules.points_per_free_month) * 100));
  const pointsNeeded = Math.max(0, rules.points_per_free_month - balance.balance);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-12" data-testid="refer-and-earn-page">
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="refer-back-btn" onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Gift className="w-6 h-6 text-emerald-500" />Refer &amp; Earn
          </h1>
          <p className="text-sm text-slate-500 font-body">{data?.promo?.headline || 'Invite friends. Earn a free month of Growth.'}</p>
        </div>
      </div>


      {/* Share card */}
      <Card className="p-6 rounded-3xl border-slate-200 mb-6" data-testid="share-card">
        <h3 className="font-heading text-base font-semibold text-slate-800 mb-3">Your invite link</h3>
        <div className="flex flex-wrap items-stretch gap-2 mb-4">
          <div className="flex-1 min-w-[240px] bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-mono text-sm text-slate-700 truncate" data-testid="share-url">
            {shareUrl}
          </div>
          <Button data-testid="copy-link-btn" onClick={copy}
            className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-heading text-sm px-5">
            {copied ? <><Check className="w-4 h-4 mr-1.5" />Copied</> : <><Copy className="w-4 h-4 mr-1.5" />Copy link</>}
          </Button>
        </div>
        <p className="text-[11px] uppercase tracking-wider font-heading text-slate-500 mb-2">Share via</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="share-whatsapp" onClick={() => shareTo('whatsapp')} variant="outline" className="rounded-full text-xs font-heading">
            <MessageCircle className="w-3.5 h-3.5 mr-1.5 text-emerald-500" /> WhatsApp
          </Button>
          <Button data-testid="share-email" onClick={() => shareTo('email')} variant="outline" className="rounded-full text-xs font-heading">
            <Mail className="w-3.5 h-3.5 mr-1.5 text-blue-500" /> Email
          </Button>
          <Button data-testid="share-twitter" onClick={() => shareTo('twitter')} variant="outline" className="rounded-full text-xs font-heading">
            <Twitter className="w-3.5 h-3.5 mr-1.5 text-sky-500" /> Twitter / X
          </Button>
          <Badge className="bg-slate-100 text-slate-600 ml-auto self-center text-[11px]">Code: <strong className="ml-1">{code}</strong></Badge>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Referral history */}
        <Card className="p-6 rounded-3xl border-slate-200" data-testid="referrals-history">
          <h3 className="font-heading text-base font-semibold text-slate-800 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-slate-500" />Your referrals</h3>
          {history.length === 0 ? (
            <p className="text-sm text-slate-500 font-body">No referrals yet. Share your link to get started.</p>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div>
                    <p className="text-sm font-body text-slate-700">{h.referee_email_masked}</p>
                    <p className="text-[11px] text-slate-400 font-body flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(h.created_at).toLocaleDateString('en-IN')}</p>
                  </div>
                  {h.status === 'qualified_paid' && <Badge className="bg-emerald-100 text-emerald-700">Paid · +{h.points_awarded}</Badge>}
                  {h.status === 'qualified_active' && <Badge className="bg-blue-100 text-blue-700">Active · +{h.points_awarded}</Badge>}
                  {h.status === 'pending' && <Badge className="bg-amber-50 text-amber-700">Pending</Badge>}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Leaderboard */}
        <Card className="p-6 rounded-3xl border-slate-200" data-testid="referrals-leaderboard">
          <h3 className="font-heading text-base font-semibold text-slate-800 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Top referrers</h3>
          {board.length === 0 ? (
            <p className="text-sm text-slate-500 font-body">Be the first on the leaderboard!</p>
          ) : (
            <div className="space-y-2">
              {board.map((row, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-xl bg-slate-50 border border-slate-100">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-heading font-bold ${i === 0 ? 'bg-amber-200 text-amber-800' : i === 1 ? 'bg-slate-200 text-slate-700' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-white border border-slate-200 text-slate-500'}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-body text-slate-800 truncate">{row.name}</p>
                    <p className="text-[11px] text-slate-400 font-body">{row.referrals} referrals</p>
                  </div>
                  <p className="font-heading text-sm font-bold text-emerald-600">{row.points} pts</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
